import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayoutMain from "../../../../layout/dashboard/DashboardLayoutMain";
import type { AppDispatch } from "../../../../redux/store";
import RootState, {
  NotificationCampaignDetails,
  NotificationCampaignPayload,
  NotificationRecurrenceFrequency,
} from "../../../../redux/type";
import {
  createNotificationCampaign,
  getNotificationCampaign,
  updateNotificationCampaign,
} from "../../../../redux/notificationCampaign";
import { ALL_ROUTES } from "../../../../routes/AllRoutes";
import {
  ChannelSelectionForm,
  CreateNotificationForm,
  EMPTY_CREATE_NOTIFICATION_FORM,
} from "../types";
import StepIndicator, { WizardStep } from "./StepIndicator";
import ChannelStep from "./ChannelStep";
import ConfigureStep from "./ConfigureStep";
import ReviewStep from "./ReviewStep";

const WIZARD_STEPS: WizardStep[] = [
  { label: "channel", title: "Channel" },
  { label: "configure", title: "Configure" },
  { label: "review", title: "Review" },
  // { label: "send", title: "Send" },
];

function buildPayload(
  form: CreateNotificationForm,
  saveAsDraft: boolean,
): NotificationCampaignPayload {
  const scheduledAt =
    form.schedule_type === "scheduled" &&
    form.schedule_date &&
    form.schedule_time
      ? new Date(
          `${form.schedule_date}T${form.schedule_time}:00Z`,
        ).toISOString()
      : null;

  return {
    save_as_draft: saveAsDraft,
    type: (form.type || "transactional") as NotificationCampaignPayload["type"],
    title: form.title,
    body: form.body,
    action_button_enabled: form.action_button_enabled,
    action_button_label: form.action_button_enabled
      ? form.action_button_label
      : null,
    action_button_url: form.action_button_enabled
      ? form.action_button_url
      : null,
    audience_type: form.audience_type,
    schedule_type: form.schedule_type,
    scheduled_at: scheduledAt,
    is_recurring: form.is_recurring,
    recurrence_frequency: form.is_recurring ? form.recurrence_frequency : null,
    csv_matched_user_ids:
      form.audience_type === "csv_upload" ? form.csv_matched_user_ids : null,
    channels: form.channels,
    filter_rules:
      form.audience_type === "filter"
        ? form.filter_rules.map((rule) => ({
            type: rule.type.toLowerCase(),
            value: rule.value,
          }))
        : null,
  };
}
export function mapCampaignDetailsToForm(
  campaign: NotificationCampaignDetails,
): CreateNotificationForm {
  let scheduleDate = "";
  let scheduleTime = "";

  if (campaign?.schedule?.scheduled_at) {
    const scheduled = new Date(campaign?.schedule?.scheduled_at);
    // Derive both date and time from the same UTC-based ISO string — the
    // field is labeled "Time (UTC)" and buildPayload() reconstructs
    // scheduled_at as literal UTC, so mixing in a local-time component here
    // would silently shift the scheduled send time on save.
    const [isoDate, isoTime] = scheduled.toISOString().split("T");
    scheduleDate = isoDate;
    scheduleTime = isoTime.slice(0, 5);
  }

  return {
    ...EMPTY_CREATE_NOTIFICATION_FORM,

    type: campaign.type as any,

    title: campaign.title,

    body: campaign.body,

    action_button_enabled: campaign.action_button?.enabled ?? false,

    action_button_label: campaign.action_button?.label ?? "",

    action_button_url: campaign.action_button?.url ?? "",

    channels: campaign.channels.map((channel) => ({
      channel: channel.channel,
      recipient_type: channel.recipient_type,
      // add the other fields your ChannelSelectionForm requires
    })) as any,

    audience_type: campaign.audience?.type as any,

    filter_rules: campaign.audience?.filter_rules ?? [],

    schedule_type: campaign.schedule?.type as any,

    schedule_date: scheduleDate,

    schedule_time: scheduleTime,

    is_recurring: campaign.schedule?.is_recurring ?? false,

    recurrence_frequency:
      (campaign.schedule
        ?.recurrence_frequency as NotificationRecurrenceFrequency) ?? null,

    csv_matched_user_ids: null,
  };
}
export default function CreateNotificationIndex() {
  const location = useLocation();
  const campaignDetails = location?.state?.details;
  const isUpdateMode =
    location.state?.action === "update" && campaignDetails.status === "draft";
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { loading_create, loading_update } = useSelector(
    (state: RootState) => state.notification_campaign,
  );
  const [step, setStep] = useState(isUpdateMode ? 3 : 1);
  const [form, setForm] = useState<CreateNotificationForm>(
    EMPTY_CREATE_NOTIFICATION_FORM,
  );

  const updateForm = <K extends keyof CreateNotificationForm>(
    field: K,
    value: CreateNotificationForm[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleChannelsChange = (channels: ChannelSelectionForm[], body: string) => {
    setForm((prev) => ({ ...prev, channels, body }));
  };

  const goBackToList = () =>
    navigate(ALL_ROUTES?.DASHBOARD_NOTIFICATION_MANAGEMENT);

  const handleSaveDraft = async () => {
    if (isUpdateMode && campaignDetails && campaignDetails.id) {
      await handleUpdate(true);
      return;
    }
    try {
      await dispatch(
        createNotificationCampaign(buildPayload(form, true)) as any,
      ).unwrap();
      goBackToList();
    } catch (error) {
      console.error("Failed to save draft:", error);
    }
  };

  const handleCreate = async () => {
    if (isUpdateMode && campaignDetails && campaignDetails.id) {
      await handleUpdate(false);
      return;
    }
    try {
      await dispatch(
        createNotificationCampaign(buildPayload(form, false)) as any,
      ).unwrap();
      goBackToList();
    } catch (error) {
      console.error("Failed to create campaign:", error);
    }
  };
  /**
   * UPDATE
   */
  const handleUpdate = async (submit: boolean) => {
    if (!campaignDetails?.id) {
      console.error("Campaign ID is missing");
      return;
    }

    const payload = buildPayload(form, submit);

    try {
      await dispatch(
        updateNotificationCampaign({
          id: campaignDetails.id,
          data: payload,
        }) as any,
      ).unwrap();
      goBackToList();
    } catch (error) {
      console.error("Failed to update campaign:", error);
    }
  };
  /**
   * Populate form when editing an existing campaign
   */
  useEffect(() => {
    const getCampaignform = async () => {
      if (campaignDetails && campaignDetails.id) {
        const result = await dispatch(
          getNotificationCampaign(campaignDetails?.id),
        ).unwrap();
        if (isUpdateMode && campaignDetails.id) {
          setForm(mapCampaignDetailsToForm(result?.data?.data));
        }
      }

     
    };
    getCampaignform();
  }, [isUpdateMode, campaignDetails?.id]);

  return (
    <DashboardLayoutMain
      back
      onBack={() => (step === 1 ? goBackToList() : setStep((prev) => prev - 1))}
      title="Notifications"
    >
      <section className="mx-[2rem] py-[16px] pb-[40px] max-w-[900px]">
        <StepIndicator steps={WIZARD_STEPS} currentStep={step} />

        {step === 1 && (
          <ChannelStep
            channels={form.channels}
            body={form.body}
            onChannelsChange={handleChannelsChange}
            onCancel={goBackToList}
            onSaveDraft={handleSaveDraft}
            onContinue={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <ConfigureStep
            form={form}
            onChange={updateForm}
            onCancel={goBackToList}
            onSaveDraft={handleSaveDraft}
            onContinue={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <ReviewStep
            form={form}
            onEditChannel={() => setStep(1)}
            onEditConfigure={() => setStep(2)}
            onCancel={goBackToList}
            onSaveDraft={handleSaveDraft}
            onCreate={handleCreate}
            creating={loading_create}
            submitLabel={"Submit"}
          />
        )}
      </section>
    </DashboardLayoutMain>
  );
}
